import React from 'react';

interface PortSlotIconProps {
  portNumber: number;
  selected?: boolean;
  disabled?: boolean;
  /**
   * Renders like a port already claimed elsewhere (grey fills) but remains clickable — used for
   * BPS-360 conflict confirmation instead of true `disabled`.
   */
  portUsedByOtherDevice?: boolean;
  onClick?: () => void;
  className?: string;
  ariaHasPopup?: 'dialog';
  ariaLabel?: string;
}

const PortSlotIcon: React.FC<PortSlotIconProps> = ({
  portNumber,
  selected = false,
  disabled = false,
  portUsedByOtherDevice = false,
  onClick,
  className,
  ariaHasPopup,
  ariaLabel,
}) => {
  const nonInteractive = disabled;
  const usedElsewhereLook = portUsedByOtherDevice && !disabled;

  return (
    <div
      className={`port-slot-icon ${className ?? ''}`}
      data-selected={selected}
      data-disabled={disabled}
      data-port-used-by-other={usedElsewhereLook ? 'true' : 'false'}
      onClick={nonInteractive ? undefined : onClick}
      role="button"
      tabIndex={nonInteractive ? -1 : 0}
      onKeyDown={(e) => {
        if (!nonInteractive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-label={ariaLabel ?? `Port ${portNumber}`}
      aria-pressed={selected}
      aria-disabled={nonInteractive}
      {...(ariaHasPopup ? { 'aria-haspopup': ariaHasPopup } : {})}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30">
        {/* Inner connector fill — color controlled via CSS .port-slot-inner */}
        <path
          className="port-slot-inner"
          d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2z"
        />
        {/* Housing outline — color controlled via CSS .port-slot-housing */}
        <path
          className="port-slot-outline"
          d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
        />
        {/* Four connector pins */}
        <path className="port-slot-pin" d="M7 16H5v4h2z" />
        <path className="port-slot-pin" d="M11 16H9v4h2z" />
        <path className="port-slot-pin" d="M15 16h-2v4h2z" />
        <path className="port-slot-pin" d="M19 16h-2v4h2z" />
        {/* Port number label */}
        <text
          className="port-slot-label"
          x="12"
          y="10.5"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="8"
          fontWeight="700"
          fontFamily="Helvetica, Arial, sans-serif"
        >
          {portNumber}
        </text>
      </svg>
    </div>
  );
};

export default PortSlotIcon;
